import React, { useCallback, useState } from 'react';
import { globalStyles, Divider } from 'snacks-design-system';
import OrderOnWaySmall from '@assets/order-on-its-way-3-small.png';
import styles from './DashboardEmptyCourierList.module.scss';
import PIEText from '@src/shared/components/PIEText';
import { useTranslation } from 'react-i18next';
import {
  CourierDetailsActionsAddInput,
  ICourierDetailErrorProps,
} from '@src/pages/Couriers/components/CourierDetailsActionsAddInput';
import PIEButton from '@src/shared/components/PIEButton';
import { useQueryClient } from 'react-query';
import { useLinkCourierMutation } from '@src/pages/Couriers/queries';
import { LINK_COURIER_ERROR, TCourier } from '@src/pages/Couriers/services/api';
import {
  trackInvitationSendStart,
  trackInvitationSendSuccess,
} from '@src/pages/Couriers/tracking/invitation';
import { useKeycloakUserState } from '@src/shared/context/keycloak-user';
import { encryptHash } from '@src/shared/utils';
import { NotificationCategories, useNotificationsActions } from '@src/shared/context/notifications';
import useOCFStatus from '@src/shared/hooks/use-ocf-status';

export const DashboardEmptyCourierList = () => {
  const {
    xdsFontSize20,
    xdsFontSize16,
    xdsFontSize14,
    xdsFontWeightExtrabold,
    xdsFontWeightRegular,
    xdsSpacingD,
    xdsSpacingA,
  } = globalStyles;
  const [inputForm, setInputForm] = useState<{
    isValidated: boolean;
    data: any;
  }>();
  const [isPromising, setIsPromising] = useState<boolean>();
  const queryCache = useQueryClient();
  const { restaurant } = useKeycloakUserState();
  const { notifyInformation } = useNotificationsActions(NotificationCategories.CourierManagement);
  const { isOCFEnabled } = useOCFStatus();

  const linkCourierMutation = useLinkCourierMutation({
    onSuccess: (data: TCourier) => {
      setClearInputForm(true);
      notifyInformation(t('manage_couriers.ADD_COURIER_SUCCESS_MESSAGE'), '');
      trackInvitationSendSuccess(
        restaurant?.language,
        restaurant?.country,
        encryptHash(data.email)
      );
      queryCache.invalidateQueries('couriers');
      setIsPromising(false);
    },
    onError: (error: any) => {
      const errorMsg: LINK_COURIER_ERROR =
        error.response?.data?.reason || LINK_COURIER_ERROR.INVALID_PARAMS;
      setInputError({
        reason: t(`addCourier.error_${errorMsg}`),
        status: true,
      });
      setIsPromising(false);
    },
  });

  const onLinkCourier = async () => {
    setIsPromising(true);
    //e.preventDefault();
    if (!inputForm?.isValidated) {
      return;
    }
    trackInvitationSendStart(
      restaurant?.language,
      restaurant?.country,
      encryptHash(inputForm.data)
    );
    linkCourierMutation.mutate({
      courierEmail: inputForm.data,
      restaurantName: restaurant?.name as string,
    });
  };

  const handleOnButtonClick = () => {
    onLinkCourier();
  };

  const [inputError, setInputError] = useState<ICourierDetailErrorProps>({
    reason: '',
    status: false,
  });
  const [clearForm, setClearInputForm] = useState(false);
  const { t } = useTranslation();
  const handleFormImputValidations = useCallback(
    (e: any) => {
      return setInputForm({ isValidated: e.isValidated, data: e.data });
    },
    [inputForm?.isValidated]
  );

  return (
    <div className={styles.dashboardEmptyCourierList}>
      <img src={OrderOnWaySmall} loading="lazy" />
      <PIEText
        fontWeight={xdsFontWeightExtrabold}
        text={t('dashboard.empty_COURIERS_TITLE')}
        size={xdsFontSize20}
      />

      {!isOCFEnabled && (
        <>
          <PIEText
            text={t('dashboard.empty_COURIERS_DESCRIPTION')}
            fontWeight={xdsFontWeightRegular}
            size={xdsFontSize16}
            align="center"
          />
          <Divider offsetSize={xdsSpacingD} />
          <CourierDetailsActionsAddInput
            isEmailExists={inputError}
            clearForm={clearForm}
            inputSize="medium"
            onOutSideClickValidationCheck={handleFormImputValidations}
          />
          <div className={styles.dashboardEmptyCourierListButtonWrapper}>
            <PIEButton
              type="submit"
              mode="primary"
              size="small"
              text={t('manage_couriers.ADD_BUTTON_TEXT')}
              disabled={!inputForm?.isValidated}
              isPromising={isPromising}
              onClick={handleOnButtonClick}
            />
          </div>
          <Divider offsetSize={xdsSpacingA} />
          <PIEText
            text={t('dashboard.empty_COURIERS_SUBTEXT')
              .replace('<url>', '<a href="/couriers" >')
              .replace('</url_end>', '</a>')}
            size={xdsFontSize14}
            align="center"
            fontWeight={xdsFontWeightRegular}
          />
        </>
      )}
    </div>
  );
};

export default DashboardEmptyCourierList;
